import {
  Journey,
  JourneyItem,
  Planner,
  PlannerAutomation,
  PlannerTabTitle,
  Position,
  Rect,
} from "./journeyPlanner.types";
import { arrayMove } from "@dnd-kit/sortable";
import {
  doJourneyCardsOverlap,
  getFilteredAutomations,
  JOURNEY_CARD_MIN_HEIGHT,
  JOURNEY_CARD_WIDTH,
} from "./journeyPlannerHelpers";

const defaultJourneyName = "New Journey";
const plannerTabs = (
  ["Daily", "Fixture", "Triggered"] as PlannerTabTitle[]
).map((title) => ({ title }));

function getDefaultPlanner(): Planner {
  return {
    Journeys: [],
    Automations: [],
  };
}

function replaceJourneyInPlanner(
  planner: Planner,
  updatedJourney: Journey
): Planner {
  return {
    ...planner,
    Journeys: planner.Journeys.map((journey) =>
      journey.Id === updatedJourney.Id ? updatedJourney : journey
    ),
  };
}

function removeJourneyFromPlanner(
  planner: Planner,
  journeyId: number
): Planner {
  return {
    ...planner,
    Journeys: planner.Journeys.filter(
      (journey) => journey.Id !== journeyId
    ),
  };
}

function addJourneyToPlanner(planner: Planner, journey: Journey): Planner {
  return {
    ...planner,
    Journeys: [...planner.Journeys, journey],
  };
}

function getPositionRect(position: Position): Rect {
  return {
    left: position.x,
    top: position.y,
    right: position.x + JOURNEY_CARD_WIDTH,
    bottom: position.y + JOURNEY_CARD_MIN_HEIGHT,
  };
}

function getJourneyRect(journey: Journey): Rect {
  return {
    left: journey.X ?? 0,
    top: journey.Y ?? 0,
    right: (journey.X ?? 0) + JOURNEY_CARD_WIDTH,
    bottom: (journey.Y ?? 0) + JOURNEY_CARD_MIN_HEIGHT,
  };
}

function isJourneyCreatePositionOccupied(
  journeys: Journey[],
  position: Position
) {
  const positionRect = getPositionRect(position);

  return journeys.some((journey) =>
    doJourneyCardsOverlap(positionRect, getJourneyRect(journey))
  );
}

function getNextJourneyCreatePosition(journeys: Journey[]): Position {
  const start = { x: 24, y: 24 };
  const gap = 24;

  if (journeys.length === 0) {
    return start;
  }

  let nextPosition = { ...start };

  while (isJourneyCreatePositionOccupied(journeys, nextPosition)) {
    nextPosition = {
      ...nextPosition,
      x: nextPosition.x + JOURNEY_CARD_WIDTH + gap,
    };
  }

  return nextPosition;
}

function removeJourneyItemFromPlanner(
  planner: Planner,
  journeyId: number,
  itemId: number
): Planner {
  return {
    ...planner,
    Journeys: planner.Journeys.map((journey) => {
      if (journey.Id !== journeyId) {
        return journey;
      }

      return {
        ...journey,
        Items: journey.Items
          .filter((item) => item.Id !== itemId)
          .map((item, index) => ({
            ...item,
            SortOrder: index + 1,
          })),
      };
    }),
  };
}

function addAutomationToJourneyPlanner(
  planner: Planner,
  journeyId: number,
  draggedAutomation: PlannerAutomation,
  nextJourneyItemId: number,
  insertIndex: number | null = null
): Planner {
  return {
    ...planner,
    Journeys: planner.Journeys.map((journey) => {
      if (journey.Id !== journeyId) {
        return journey;
      }

      const currentItems = journey.Items || [];
      const nextItem: JourneyItem = {
        Id: nextJourneyItemId,
        AutomationJourneyId: journey.Id,
        AutomationId: draggedAutomation.Id,
        SortOrder: currentItems.length + 1,
        AutomationName: draggedAutomation.Name,
        AutomationType: draggedAutomation.Type,
        ScheduleMode: draggedAutomation.ScheduleMode,
        Status: draggedAutomation.Status,
        CommunicationName: draggedAutomation.CommunicationName,
        CampaignName: draggedAutomation.CampaignName,
      };
      const nextItems = [...currentItems];
      const targetIndex =
        insertIndex === null || insertIndex === undefined
          ? nextItems.length
          : Math.max(0, Math.min(insertIndex, nextItems.length));

      nextItems.splice(targetIndex, 0, nextItem);

      return {
        ...journey,
        Items: nextItems.map((item, index) => ({
          ...item,
          SortOrder: index + 1,
        })),
      };
    }),
  };
}

function reorderJourneyItemsInPlanner(
  planner: Planner,
  journeyId: number,
  oldIndex: number,
  newIndex: number
): Planner {
  return {
    ...planner,
    Journeys: planner.Journeys.map((journey) => {
      if (journey.Id !== journeyId) {
        return journey;
      }

      return {
        ...journey,
        Items: arrayMove(
          journey.Items,
          oldIndex,
          newIndex
        ).map((item, index) => ({
          ...item,
          SortOrder: index + 1,
        })),
      };
    }),
  };
}

function buildSavePlannerPayload(journeys: Journey[]) {
  return {
    journeys: journeys.map((journey) => ({
      id: journey.Id,
      name: journey.Name,
      x: Math.round(journey.X ?? 0),
      y: Math.round(journey.Y ?? 0),
      items: (journey.Items || []).map((item, index) => ({
        id: item.Id,
        automationId: item.AutomationId,
        sortOrder: index + 1,
      })),
    })),
  };
}

function getFilteredPlannerAutomations(planner: Planner, activeTab: string) {
  return getFilteredAutomations(planner.Automations, activeTab);
}

export {
  addAutomationToJourneyPlanner,
  addJourneyToPlanner,
  buildSavePlannerPayload,
  defaultJourneyName,
  getDefaultPlanner,
  getFilteredPlannerAutomations,
  getNextJourneyCreatePosition,
  plannerTabs,
  removeJourneyFromPlanner,
  removeJourneyItemFromPlanner,
  reorderJourneyItemsInPlanner,
  replaceJourneyInPlanner,
};
